import { getMarketingPageContent } from './marketingContent';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://vireinoiva.com.br').replace(/\/+$/, '');
const SITE_NAME = 'Virei Noiva';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

export function buildCanonicalUrl(path = '/') {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalizedPath}`;
}

function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.ico`,
  };
}

function buildWebsiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  };
}

function buildSoftwareApplicationJsonLd({
  name,
  description,
  path,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
    },
    description,
    url: buildCanonicalUrl(path),
    image: DEFAULT_IMAGE,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
  };
}

function buildBreadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: buildCanonicalUrl(item.path),
    })),
  };
}

function buildFaqJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

const MARKETING_PAGES = {
  home: {
    path: '/',
    title: 'Virei Noiva | Site de casamento com lista de presentes',
    description:
      'Crie seu site de casamento com lista de presentes, RSVP, convite digital, Save the Date e painel financeiro.',
    jsonLd: [
      buildOrganizationJsonLd(),
      buildWebsiteJsonLd(),
    ],
  },
  login: {
    path: '/login',
    title: 'Entrar | Virei Noiva',
    description: 'Acesse seu painel para editar o site de casamento, gerenciar convidados, presentes e confirmações.',
  },
  register: {
    path: '/register',
    title: 'Criar conta | Virei Noiva',
    description: 'Crie sua conta para montar o site de casamento, receber presentes online e acompanhar RSVP em um só lugar.',
  },
  plans: {
    path: '/planos',
    title: 'Planos e taxa de 3,59% | Virei Noiva',
    description: 'Conheça o plano do Virei Noiva para criar seu site de casamento e receber presentes com taxa clara de 3,59%.',
    jsonLd: [
      buildSoftwareApplicationJsonLd({
        name: 'Planos Virei Noiva',
        description:
          'Plano para criar site de casamento, lista de presentes, RSVP e painel financeiro com taxa clara por presente.',
        path: '/planos',
      }),
      buildBreadcrumbJsonLd([
        { name: 'Home', path: '/' },
        { name: 'Planos', path: '/planos' },
      ]),
    ],
  },
  weddingSite: {
    path: '/site-de-casamento',
    title: 'Site de casamento personalizado | Virei Noiva',
    description:
      'Crie um site de casamento elegante para compartilhar história, fotos, local, RSVP e lista de presentes.',
  },
  giftList: {
    path: '/lista-de-presentes-casamento',
    title: 'Lista de presentes de casamento online | Virei Noiva',
    description:
      'Monte sua lista de presentes de casamento online e acompanhe os recebimentos de forma simples.',
  },
  digitalInvite: {
    path: '/convite-digital-casamento',
    title: 'Convite digital de casamento | Virei Noiva',
    description:
      'Crie convites digitais elegantes para compartilhar com seus convidados.',
  },
  saveTheDate: {
    path: '/save-the-date',
    title: 'Save the Date de casamento online | Virei Noiva',
    description:
      'Avise seus convidados com um Save the Date digital personalizado.',
  },
  rsvp: {
    path: '/rsvp-casamento',
    title: 'RSVP para casamento online | Virei Noiva',
    description:
      'Organize confirmações de presença com um RSVP online simples para convidados e fácil de acompanhar.',
  },
  pixGift: {
    path: '/presente-de-casamento-pix',
    title: 'Presente de casamento via PIX | Virei Noiva',
    description:
      'Receba presentes de casamento via PIX com um site elegante, lista online e painel para acompanhar os recebimentos.',
  },
};

Object.values(MARKETING_PAGES).forEach((page) => {
  if (page.path === '/') return;
  page.jsonLd = [
    buildSoftwareApplicationJsonLd({
      name: page.title.replace(' | Virei Noiva', ''),
      description: page.description,
      path: page.path,
    }),
    buildBreadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: page.title.replace(' | Virei Noiva', ''), path: page.path },
    ]),
    ...(page.jsonLd || []),
  ];
});

export function getMarketingSeoConfig(key) {
  const page = MARKETING_PAGES[key] || MARKETING_PAGES.home;
  const pageContent = getMarketingPageContent(key);
  const faqJsonLd = pageContent?.faqItems?.length
    ? [buildFaqJsonLd(pageContent.faqItems)]
    : [];
  return {
    title: page.title,
    description: page.description,
    canonical: buildCanonicalUrl(page.path),
    jsonLd: [...(page.jsonLd || []), ...faqJsonLd],
  };
}

export function getPublicSiteSeoConfig(site, slug, options = {}) {
  const coupleNames = String(site?.couple_names || 'Site de casamento').trim();
  const description =
    String(site?.story_text || '').trim().slice(0, 160) ||
    `Acompanhe os detalhes do casamento de ${coupleNames}, com local, fotos, RSVP e lista de presentes.`;

  return {
    title: `${coupleNames} | Site de casamento | ${SITE_NAME}`,
    description,
    canonical: buildCanonicalUrl(`/s/${encodeURIComponent(slug || '')}`),
    noindex: Boolean(options.noindex),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: coupleNames,
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        startDate: site?.wedding_date || undefined,
        location: {
          '@type': 'Place',
          name: site?.ceremony_name || site?.reception_name || 'Local do casamento',
          address: site?.ceremony_address || site?.reception_address || undefined,
        },
        organizer: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: SITE_URL,
        },
        url: buildCanonicalUrl(`/s/${encodeURIComponent(slug || '')}`),
      },
    ],
  };
}
